/**
 * CurrencyInput — Brazilian-style currency input
 *
 * Behaviour: digits-only entry, automatically places the decimal separator.
 * Typing "1", "2", "3" produces "0,01" → "0,12" → "1,23" (always 2 decimal places).
 *
 * Props:
 *   value    — numeric string in dot-notation ("123.45") or empty string
 *   onChange — called with numeric string ("123.45") on every change
 *
 * All other <input> HTML attributes are forwarded unchanged.
 */

import { useRef, type InputHTMLAttributes } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'

/** Maps app locale to its default currency code. */
export const LOCALE_CURRENCY: Record<string, string> = {
  'pt-BR': 'BRL',
  'en-US': 'USD',
  'es-ES': 'EUR',
}

interface CurrencyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string
  onChange: (value: string) => void
  currency?: string   // default derived from locale, fallback 'BRL'
  locale?: string     // default 'pt-BR'
  error?: boolean
}

/** Convert raw cents integer (e.g. 12345) to formatted display ("123,45") */
function centsToDisplay(cents: number, locale: string, currency: string): string {
  return new Intl.NumberFormat(locale, {
    style:                 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

/** Convert numeric string ("123.45") to raw cents integer (12345) */
function valueToCents(value: string): number {
  const n = parseFloat(value)
  return isNaN(n) ? 0 : Math.round(n * 100)
}

export function CurrencyInput({
  value,
  onChange,
  locale:   localeProp,
  currency,
  error    = false,
  className = '',
  disabled,
  ...rest
}: CurrencyInputProps) {
  // Always read locale from context so the symbol updates instantly when
  // the user switches language — the prop is only an override.
  let contextLocale = 'pt-BR'
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { locale } = useTranslation()
    contextLocale = locale
  } catch {
    // CurrencyInput used outside TranslationProvider: fall back gracefully
  }
  const locale = localeProp ?? contextLocale
  const resolvedCurrency = currency ?? LOCALE_CURRENCY[locale] ?? 'BRL'
  // We keep a mutable cents ref so we can mutate without re-render on every digit
  const centsRef = useRef<number>(valueToCents(value))

  // Sync ref when value changes externally (e.g. form reset)
  const externalCents = valueToCents(value)
  if (centsRef.current !== externalCents) {
    centsRef.current = externalCents
  }

  const displayValue = centsRef.current === 0 && value === ''
    ? ''
    : centsToDisplay(centsRef.current, locale, resolvedCurrency)

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return

    if (e.key === 'Backspace') {
      e.preventDefault()
      centsRef.current = Math.floor(centsRef.current / 10)
      onChange(centsRef.current === 0 ? '' : String(centsRef.current / 100))
      return
    }

    if (e.key === 'Delete') {
      e.preventDefault()
      centsRef.current = 0
      onChange('')
      return
    }

    if (!/^\d$/.test(e.key)) return  // ignore non-digit keys (arrows, tab, etc.)

    e.preventDefault()
    const digit = parseInt(e.key, 10)
    // Prevent overflow: max 10 digits total (99 999 999.99)
    if (centsRef.current >= 9_999_999_99) return

    centsRef.current = centsRef.current * 10 + digit
    onChange(String(centsRef.current / 100))
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    if (disabled) return
    const pasted = e.clipboardData.getData('text')
    // Accept pasted numeric strings with either comma or dot as decimal
    const normalized = pasted.replace(/[^\d,.-]/g, '').replace(',', '.')
    const n = parseFloat(normalized)
    if (!isNaN(n)) {
      centsRef.current = Math.round(n * 100)
      onChange(String(centsRef.current / 100))
    }
  }

  return (
    <input
      {...rest}
      type="text"
      inputMode="numeric"
      disabled={disabled}
      value={displayValue}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={() => {/* controlled via keyDown */}}
      className={[
        'w-full px-4 py-2 rounded-lg border',
        error
          ? 'border-red-300'
          : 'border-slate-300 dark:border-slate-600',
        'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
        'focus:outline-none focus:ring-2 focus:ring-emerald-500',
        'tabular-nums',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className,
      ].join(' ')}
    />
  )
}
